import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Flame, Calendar } from "lucide-react";

/** GitHub-style contribution grid. 53 cols (weeks) × 7 rows (days). */
export default function StreakCalendar({ days = 365 }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/stats/streak-calendar`, { params: { days } }).then((r) => setData(r.data)).catch(() => {});
  }, [days]);

  const grid = useMemo(() => {
    if (!data?.days) return [];
    // Pad the start so column 0, row 0 lines up with Sunday
    const startDate = new Date(data.days[0].date + "T00:00:00Z");
    const startDow = startDate.getUTCDay(); // 0=Sun
    const padded = [...Array(startDow).fill(null), ...data.days];
    // Chunk into columns of 7
    const cols = [];
    for (let i = 0; i < padded.length; i += 7) cols.push(padded.slice(i, i + 7));
    return cols;
  }, [data]);

  if (!data) return null;
  const max = Math.max(1, data.max_count || 1);
  const colorFor = (n) => {
    if (n === 0) return "bg-secondary/50 border-border/50";
    const ratio = n / max;
    if (ratio > 0.75) return "bg-brand-gold border-brand-gold shadow-[0_0_10px_-2px_rgba(255,184,0,0.7)]";
    if (ratio > 0.5)  return "bg-brand-gold/70 border-brand-gold/60";
    if (ratio > 0.25) return "bg-brand-gold/40 border-brand-gold/30";
    return "bg-brand-gold/20 border-brand-gold/20";
  };

  return (
    <section className="border border-border rounded-2xl p-6 sm:p-8 bg-card" data-testid="streak-calendar">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-2 flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5" /> Practice streak
          </div>
          <h3 className="font-display text-2xl sm:text-3xl font-black tracking-tight">
            {data.active_days} <span className="text-muted-foreground">/ {data.total_days} days practised</span>
          </h3>
          <p className="mt-1 text-xs font-mono text-muted-foreground">
            One tile per day · brighter = more attempts
          </p>
        </div>
        {data.max_count > 0 && (
          <div className="inline-flex items-center gap-2 px-3 h-9 border border-brand-gold/40 bg-brand-gold/10 rounded-lg text-[10px] uppercase tracking-widest font-bold text-brand-gold">
            <Flame className="w-3.5 h-3.5" /> Best day: {data.max_count} attempts
          </div>
        )}
      </div>

      <div className="overflow-x-auto -mx-2 px-2">
        <div className="inline-flex gap-[3px] pb-2">
          {grid.map((week, ci) => (
            <div key={ci} className="flex flex-col gap-[3px]">
              {Array.from({ length: 7 }).map((_, ri) => {
                const cell = week[ri];
                if (!cell) return <div key={ri} className="w-[13px] h-[13px]" />;
                return (
                  <div
                    key={ri}
                    title={`${cell.date} · ${cell.count} attempt${cell.count === 1 ? "" : "s"}`}
                    data-testid={`streak-day-${cell.date}`}
                    data-count={cell.count}
                    className={`w-[13px] h-[13px] rounded-sm border ${colorFor(cell.count)} transition-transform hover:scale-125 cursor-help`}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-5 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
        Less
        <div className="w-3 h-3 rounded-sm bg-secondary/50 border border-border/50" />
        <div className="w-3 h-3 rounded-sm bg-brand-gold/20 border border-brand-gold/20" />
        <div className="w-3 h-3 rounded-sm bg-brand-gold/40 border border-brand-gold/30" />
        <div className="w-3 h-3 rounded-sm bg-brand-gold/70 border border-brand-gold/60" />
        <div className="w-3 h-3 rounded-sm bg-brand-gold border border-brand-gold" />
        More
      </div>
    </section>
  );
}
